import { AuditOutlined, BgColorsOutlined, CreditCardOutlined, DownOutlined, FileTextOutlined, GlobalOutlined, InfoCircleOutlined, LockOutlined, LogoutOutlined, UserOutlined, ZoomInOutlined } from "@ant-design/icons";
import { Dropdown, Typography } from "../ui";
import type { MenuProps } from "../ui";
import type { AppShellAction } from "./AppShell";
import { appShellMetrics, typography } from "./spacing";
import { ThemeDrawer } from "../theme/ThemeDrawer";
import { useOptionalAppTheme } from "../theme/useAppTheme";
import { DISPLAY_ZOOM_OPTIONS, displayZoomLabel } from "../theme/displayZoom";
import { useState } from "react";
import { appShellLanguages, languageCatalog, languageMenuLabels, type AppShellLanguage } from "../i18n/languages";
import type { Lang } from "../i18n/i18n";
import { billingNavigationTitle } from "../billing/billingNavigationTranslations";
import type { AppShellLegalLink } from "./AppShellNavigationFooter";

/** Initials taken from a one-part name. */
const SINGLE_NAME_INITIALS_LENGTH = 2;

export type AppShellUserMenuProps = {
  displayName: string;
  openLabel: string;
  /** Auslöser: `icon` nur Benutzersymbol (Standard); `name` Initialen, Name und Pfeil, in der mobilen Kopfzeile nur Initialen. */
  trigger?: "icon" | "name";
  identity?: { contextLabel: string; context: string };
  /** Öffnet die Theme-Auswahl (`ThemeDrawer`); setzt den gemeinsamen `ThemeProvider` voraus. Texte aus `themeTranslations`. */
  theme?: { label: string; tr: (key: string) => string };
  /** Gerätelokale Anzeigegröße (`useDisplayZoom`) als eigener Menüpunkt. */
  zoom?: { label: string; value: number; onChange: (value: number) => void };
  language?: { value: Lang; onChange: (language: AppShellLanguage) => void };
  /** Sprachwahl über die gemeinsamen AppShell-Sprachen hinaus (z. B. my-vitalteam mit fünf Sprachen); ersetzt `language`. */
  languages?: { value: Lang; options: readonly Lang[]; onChange: (language: Lang) => void };
  userSettings?: AppShellAction;
  changePassword?: AppShellAction;
  information?: AppShellAction;
  billing?: Omit<AppShellAction, "label">;
  logs?: AppShellAction;
  /** AGB, Datenschutz, Impressum als Untermenü; der App-Rahmen setzt hier die Rechtstexte aus `navigationFooter.legal` ein. */
  legal?: { label: string; links: AppShellLegalLink[] };
  logout: AppShellAction;
};

/** Initialen für den Auslöser im Modus `name`: erster und letzter Namensteil, sonst die ersten zwei Zeichen. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0]?.slice(0, SINGLE_NAME_INITIALS_LENGTH) ?? "";
  return letters.toUpperCase() || "·";
}

/** Stable order and icons; consumers supply translated labels and authorized actions. */
export function AppShellUserMenu({ displayName, openLabel, trigger = "icon", identity, theme, zoom, language: sharedLanguage, languages, userSettings, changePassword, information, billing, logs, legal, logout }: AppShellUserMenuProps) {
  const appTheme = useOptionalAppTheme();
  const showName = trigger === "name";
  const language = languages ?? (sharedLanguage ? { value: sharedLanguage.value, options: appShellLanguages, onChange: sharedLanguage.onChange as (next: Lang) => void } : undefined);
  const [menuOpen, setMenuOpen] = useState(false);
  const items: MenuProps["items"] = [
    {
      key: "identity", label: (
        <div style={{ display: "flex", flexDirection: "column", gap: appShellMetrics.pinGap, maxWidth: appShellMetrics.userMenuWidth, paddingBlock: appShellMetrics.identityPadding }}>
          {identity ? <Typography.Text type="secondary" style={{ fontSize: typography.sm, lineHeight: typography.lineHeightBase }}>{identity.contextLabel}: {identity.context}</Typography.Text> : null}
          <Typography.Text style={{ fontSize: typography.md, lineHeight: typography.lineHeightBase, wordBreak: "break-all" }}>{displayName}</Typography.Text>
        </div>
      ), disabled: true
    },
    { type: "divider" },
    ...(theme ? [{ key: "theme", icon: <BgColorsOutlined />, label: theme.label, onClick: () => appTheme?.openThemeDrawer(theme.tr) }] : []),
    ...(zoom ? [{ key: "zoom", icon: <ZoomInOutlined />, label: `${zoom.label}: ${displayZoomLabel(zoom.value)}`, children: DISPLAY_ZOOM_OPTIONS.map((value) => ({ key: `zoom-${value}`, label: displayZoomLabel(value), disabled: value === zoom.value, onClick: () => zoom.onChange(value) })) }] : []),
    ...(language ? [{ key: "language", icon: <GlobalOutlined />, label: languageMenuLabels[language.value], children: language.options.map((code) => ({ key: `language-${code}`, label: <>{languageCatalog[code].symbol} {languageCatalog[code].names[code]}</>, disabled: code === language.value, onClick: () => language.onChange(code) })) }] : []),
    ...(userSettings ? [{ key: "userSettings", icon: <UserOutlined />, ...userSettings }] : []),
    ...(changePassword ? [{ key: "changePassword", icon: <LockOutlined />, ...changePassword }] : []),
    ...(information ? [{ key: "information", icon: <InfoCircleOutlined />, ...information }] : []),
    ...(billing ? [{ key: "billing", icon: <CreditCardOutlined />, label: billingNavigationTitle(language?.value ?? "de"), onClick: billing.onClick, disabled: billing.disabled }] : []),
    ...(logs ? [{ key: "logs", icon: <FileTextOutlined />, ...logs }] : []),
    ...(legal?.links.length ? [{ key: "legal", icon: <AuditOutlined />, label: legal.label, children: legal.links.map((link) => ({ key: `legal-${link.key}`, label: link.label, disabled: link.current, onClick: link.onClick })) }] : []),
    { type: "divider" },
    { key: "logout", icon: <LogoutOutlined />, danger: true, ...logout },
  ];
  return (
    <>
      <Dropdown open={menuOpen} onOpenChange={setMenuOpen} menu={{ items, onClick: () => setMenuOpen(false) }} trigger={["click"]}>
        {showName ? (
          <button type="button" className="mq-frame-profile mq-frame-profile--name" title={displayName} aria-label={openLabel}>
            <span className="mq-frame-avatar mq-frame-avatar--initials" aria-hidden>{initialsOf(displayName)}</span>
            <span className="mq-frame-profile-name">{displayName}</span>
            <DownOutlined className="mq-frame-profile-chevron" aria-hidden />
          </button>
        ) : (
          <button type="button" className="mq-frame-profile" title={displayName} aria-label={openLabel}>
            <span className="mq-frame-avatar" aria-hidden><UserOutlined /></span>
          </button>
        )}
      </Dropdown>
      {theme && appTheme ? <ThemeDrawer open={appTheme.themeDrawer !== null} initialThemeId={appTheme.themeDrawer?.initialThemeId} onClose={appTheme.closeThemeDrawer} tr={appTheme.themeDrawer?.tr ?? theme.tr} /> : null}
    </>
  );
}
